import type { Href } from "expo-router";
import { COLORS } from "../../lib/brand";
import { router } from "expo-router";
import { useEffect, useState } from "react";
import {
  ActivityIndicator,
  Modal,
  Platform,
  Pressable,
  Text,
  useWindowDimensions,
  View,
  type ViewStyle,
} from "react-native";
import Svg, { Path, Rect } from "react-native-svg";

import { useAuth } from "../../lib/authContext";
import { firstNameFromDisplay } from "../../lib/studentPortalUi";

const FOREST = COLORS.FOREST.hex;
const FOREST_DEEP = COLORS.FOREST_DEEP.hex;
const AMBER = COLORS.AMBER.hex;
const LINEN = COLORS.LINEN.hex;

const KEY_WELCOME_SEEN = "coravu.welcomeSeen.v1";
// Per-user dismissal flag for the heads-up overlay. User-scoped so it
// can't bleed across accounts on a shared browser, and localStorage
// survives Vercel rebuilds (rebuilds invalidate code, not user-scoped
// browser storage). Falls back to "treat as dismissed" on native — the
// overlay is a web-only first-run nudge.
function overlayKey(userId: string): string {
  return `coravu.welcomeOverlay.dismissed.${userId}`;
}

function readWelcomeSeen(): boolean {
  if (Platform.OS !== "web") return true;
  try {
    return typeof window !== "undefined" && window.localStorage?.getItem(KEY_WELCOME_SEEN) === "1";
  } catch {
    return false;
  }
}

function markWelcomeSeen(): void {
  if (Platform.OS !== "web") return;
  try {
    if (typeof window !== "undefined") {
      window.localStorage?.setItem(KEY_WELCOME_SEEN, "1");
    }
  } catch {
    // ignore
  }
}

function readOverlayDismissed(userId: string | null | undefined): boolean {
  if (Platform.OS !== "web" || !userId) return true;
  try {
    return typeof window !== "undefined" && window.localStorage?.getItem(overlayKey(userId)) === "1";
  } catch {
    return false;
  }
}

function markOverlayDismissed(userId: string): void {
  if (Platform.OS !== "web") return;
  try {
    if (typeof window !== "undefined") {
      window.localStorage?.setItem(overlayKey(userId), "1");
    }
  } catch {
    // ignore
  }
}

const STEPS = [
  { title: "Enroll and Prepare", Icon: IconCompete },
  { title: "Compete", Icon: IconSubmit },
  { title: "Win Prizes & Get Hired", Icon: IconHire },
] as const;

export default function WelcomeScreen() {
  const { user, loading: authLoading } = useAuth();
  const { width: winW } = useWindowDimensions();
  const wide = winW >= 760;
  const [overlayOpen, setOverlayOpen] = useState(false);

  // Unauthenticated → login. Already-seen → straight to /student.
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      router.replace("/login" as Href);
      return;
    }
    if (readWelcomeSeen()) {
      router.replace("/student" as Href);
    }
  }, [authLoading, user]);

  // Show the heads-up overlay on the user's first visit. Per-user
  // localStorage flag; once dismissed it never returns for that account.
  useEffect(() => {
    if (authLoading || !user) return;
    if (!readOverlayDismissed(user.id)) {
      setOverlayOpen(true);
    }
  }, [authLoading, user]);

  function dismissOverlay() {
    if (user) markOverlayDismissed(user.id);
    setOverlayOpen(false);
  }

  function onContinue() {
    markWelcomeSeen();
    if (user) markOverlayDismissed(user.id);
    router.replace("/student" as Href);
  }

  if (authLoading || !user) {
    return (
      <View className="flex-1 items-center justify-center bg-forest">
        <ActivityIndicator color={AMBER} size="large" />
      </View>
    );
  }

  const firstName = firstNameFromDisplay(
    (user.user_metadata?.full_name as string | undefined)?.trim() ||
      user.email?.split("@")[0] ||
      "there"
  );

  return (
    <View className="flex-1" style={{ backgroundColor: FOREST }}>
      <View
        className="flex-1"
        style={{ paddingHorizontal: 24, paddingTop: 96, paddingBottom: 96 }}
      >
        <View className="mx-auto w-full max-w-[960px] flex-1">
          {/* Greeting */}
          <Text
            className="text-[40px] leading-[1.1] text-linen"
            style={{ fontFamily: "CormorantGaramond_400Regular" }}
          >
            Hello, <Text style={{ color: AMBER }}>{firstName}</Text>.
          </Text>

          {/* Flex spacer pushes the steps toward the middle of the page */}
          <View className="flex-1" style={{ minHeight: 48 }} />

          {/* 3 step cards — horizontal grid on wide, stacked on narrow */}
          <View
            style={{
              flexDirection: wide ? "row" : "column",
              gap: wide ? 18 : 14,
              alignItems: "stretch",
            }}
          >
            {STEPS.map((s, idx) => (
              <StepCard key={s.title} index={idx + 1} title={s.title} Icon={s.Icon} />
            ))}
          </View>

          {/* Flex spacer pushes the arrow button toward the bottom */}
          <View className="flex-1" style={{ minHeight: 48 }} />

          {/* Arrow-only continue button */}
          <View style={{ alignItems: wide ? "flex-end" : "center" }}>
            <ArrowButton onPress={onContinue} />
          </View>
        </View>
      </View>

      {/* Heads-up overlay — blur-backdrop modal on first visit. Dismissal is
          per-user and persisted in localStorage, so a Vercel rebuild doesn't
          bring it back. */}
      <Modal
        visible={overlayOpen}
        transparent
        animationType="fade"
        onRequestClose={dismissOverlay}
      >
        <Pressable
          onPress={dismissOverlay}
          style={[
            {
              flex: 1,
              backgroundColor: "rgba(8,8,8,0.55)",
              alignItems: "center",
              justifyContent: "center",
              paddingHorizontal: 24,
            },
            Platform.OS === "web"
              ? ({
                  backdropFilter: "blur(8px)",
                  WebkitBackdropFilter: "blur(8px)",
                } as ViewStyle)
              : null,
          ]}
        >
          <Pressable
            onPress={(e) => e.stopPropagation?.()}
            style={{
              width: "100%",
              maxWidth: 480,
              padding: 32,
              borderRadius: 8,
              borderWidth: 1,
              borderColor: "rgba(181,135,58,0.5)",
              backgroundColor: FOREST_DEEP,
            }}
          >
            <Text
              style={{
                fontFamily: "CormorantGaramond_500Medium",
                fontSize: 24,
                lineHeight: 30,
                color: AMBER,
                marginBottom: 12,
              }}
            >
              Heads up.
            </Text>
            <Text
              style={{
                fontFamily: "DMSans_300Light",
                fontSize: 14,
                lineHeight: 22,
                color: "rgba(247,244,238,0.8)",
                marginBottom: 24,
              }}
            >
              Coravu connects students with real case competitions hosted by real
              companies. Sixty seconds on how it works, then you&apos;re off.
            </Text>
            <Pressable
              accessibilityRole="button"
              onPress={dismissOverlay}
              style={({ hovered, pressed }) => [
                {
                  alignSelf: "flex-start",
                  paddingVertical: 10,
                  paddingHorizontal: 22,
                  borderRadius: 999,
                  backgroundColor: AMBER,
                },
                Platform.OS === "web" && hovered ? { backgroundColor: COLORS.AMBER_WARM.hex } : null,
                pressed ? { opacity: 0.9 } : null,
              ]}
            >
              <Text
                style={{
                  fontFamily: "DMSans_700Bold",
                  fontSize: 12,
                  letterSpacing: 1.4,
                  textTransform: "uppercase",
                  color: FOREST,
                }}
              >
                Got it
              </Text>
            </Pressable>
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
}

function StepCard({
  index,
  title,
  Icon,
}: {
  index: number;
  title: string;
  Icon: (props: { color: string }) => React.JSX.Element;
}) {
  return (
    <View
      style={{
        flex: 1,
        minWidth: 200,
        padding: 28,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: "rgba(181,135,58,0.22)",
        backgroundColor: "rgba(181,135,58,0.04)",
        alignItems: "center",
      }}
    >
      <View
        style={{
          width: 48,
          height: 48,
          borderRadius: 8,
          borderWidth: 1,
          borderColor: "rgba(181,135,58,0.32)",
          backgroundColor: "rgba(181,135,58,0.08)",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: 18,
        }}
      >
        <Icon color={AMBER} />
      </View>
      <Text
        style={{
          fontFamily: "DMSans_700Bold",
          fontSize: 10,
          letterSpacing: 1.8,
          textTransform: "uppercase",
          color: AMBER,
          marginBottom: 6,
        }}
      >
        Step {index}
      </Text>
      <Text
        style={{
          fontFamily: "CormorantGaramond_500Medium",
          fontSize: 22,
          lineHeight: 26,
          color: LINEN,
          textAlign: "center",
        }}
      >
        {title}
      </Text>
    </View>
  );
}

function ArrowButton({ onPress }: { onPress: () => void }) {
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel="Continue to dashboard"
      style={({ hovered, pressed }) => [
        {
          width: 56,
          height: 56,
          borderRadius: 28,
          backgroundColor: AMBER,
          alignItems: "center",
          justifyContent: "center",
        },
        Platform.OS === "web"
          ? ({
              transitionProperty: "transform, background-color, box-shadow",
              transitionDuration: "160ms",
              transitionTimingFunction: "ease-out",
              cursor: "pointer",
            } as ViewStyle)
          : null,
        Platform.OS === "web" && hovered
          ? ({
              transform: [{ scale: 1.05 }],
              backgroundColor: COLORS.AMBER_WARM.hex,
              boxShadow: "0 8px 22px rgba(181,135,58,0.35)",
            } as ViewStyle)
          : null,
        pressed ? { transform: [{ scale: 0.96 }] } : null,
      ]}
    >
      <Svg width={24} height={24} viewBox="0 0 24 24" fill="none">
        <Path
          d="M5 12h13M13 5l7 7-7 7"
          stroke={LINEN}
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </Svg>
    </Pressable>
  );
}

function IconCompete({ color }: { color: string }) {
  return (
    <Svg width={22} height={22} viewBox="0 0 22 22" fill="none">
      <Path
        d="M11 2.4l2.6 5.4 5.9.9-4.3 4.2 1 5.8L11 16l-5.2 2.7 1-5.8L2.5 8.7l5.9-.9z"
        stroke={color}
        strokeWidth={1.4}
        strokeLinejoin="round"
      />
    </Svg>
  );
}

function IconSubmit({ color }: { color: string }) {
  return (
    <Svg width={22} height={22} viewBox="0 0 22 22" fill="none">
      <Path d="M19 12v4.5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V12" stroke={color} strokeWidth={1.4} strokeLinecap="round" />
      <Path d="M11 2.5v11M6.5 7L11 2.5 15.5 7" stroke={color} strokeWidth={1.4} strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

function IconHire({ color }: { color: string }) {
  return (
    <Svg width={22} height={22} viewBox="0 0 22 22" fill="none">
      <Rect x={3} y={6.5} width={16} height={12.5} rx={1.5} stroke={color} strokeWidth={1.4} />
      <Path d="M8 6.5V4.5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" stroke={color} strokeWidth={1.4} strokeLinecap="round" />
      <Path d="M3 12h16" stroke={color} strokeWidth={1.4} strokeLinecap="round" />
      <Path d="M10 11v2.5h2V11" stroke={color} strokeWidth={1.4} strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}
